// BookingPageList.js - List of Booking Page Configurations

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert,
  Stack, IconButton, Card, CardContent, CardActions, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  EventAvailable as BookingIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  AccessTime as DurationIcon,
  ConfirmationNumber as CountIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { bookingPageService } from './activityCommService';
import {
  BookingPageStatusLabels, BookingPageStatusColors,
  formatDate, truncateText
} from './activityCommEnums';

const BookingPageList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = 'Booking Pages | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadPages();
    }
  }, [canView]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadPages = async () => {
    setLoading(true);
    try {
      const res = await bookingPageService.getAll();
      setPages(res.data || res || []);
    } catch (err) {
      enqueueSnackbar('Failed to load booking pages', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId) => {
    try {
      await bookingPageService.delete(pageId);
      enqueueSnackbar('Booking page deleted', { variant: 'success' });
      setConfirmDelete(null);
      loadPages();
    } catch (err) {
      enqueueSnackbar('Failed to delete booking page', { variant: 'error' });
      console.error(err);
    }
  };

  const handleCopyLink = (page) => {
    const publicUrl = page.public_url || `${window.location.origin}/book/${page.booking_uuid || page.slug}`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      enqueueSnackbar('Booking link copied to clipboard', { variant: 'success' });
    }).catch(() => {
      enqueueSnackbar('Failed to copy link', { variant: 'error' });
    });
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/activity-dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <BookingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Booking Pages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage shareable booking pages
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages/new`)}
          >
            Create Booking Page
          </Button>
        )}
      </Box>

      {/* Pages Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (Array.isArray(pages) && pages.length > 0) ? (
        <Grid container spacing={3}>
          {pages.map((page) => {
            const publicUrl = page.public_url || `${window.location.origin}/book/${page.booking_uuid || page.slug}`;
            return (
              <Grid item xs={12} sm={6} md={4} key={page.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: '0.3s',
                    '&:hover': { boxShadow: 4 }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {page.title}
                      </Typography>
                      <Chip
                        label={BookingPageStatusLabels[page.status] || page.status || 'Active'}
                        color={BookingPageStatusColors[page.status] || 'success'}
                        size="small"
                      />
                    </Box>

                    {page.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {truncateText(page.description, 120)}
                      </Typography>
                    )}

                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DurationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {page.duration || 30} min
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CountIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {page.booking_count || 0} bookings
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatDate(page.created_at)}
                      </Typography>
                    </Stack>

                    {/* Public URL */}
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 2, p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'grey.50'
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {publicUrl}
                      </Typography>
                      <Tooltip title="Copy link">
                        <IconButton size="small" onClick={() => handleCopyLink(page)}>
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  </CardContent>

                  {canModify && (
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages/${page.id}/edit`)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CopyIcon />}
                        onClick={() => handleCopyLink(page)}
                      >
                        Copy Link
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setConfirmDelete(page.id)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <BookingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No booking pages yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first booking page to let people schedule meetings with you.
          </Typography>
          {canModify && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/booking-pages/new`)}
            >
              Create Booking Page
            </Button>
          )}
        </Paper>
      )}

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete Booking Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this booking page? This action cannot be undone
            and the public booking link will stop working.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(confirmDelete)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingPageList;
